import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { List, DownloadSimple, ChatText } from 'phosphor-react';
import { motion, AnimatePresence } from 'framer-motion';
import * as XLSX from 'xlsx';
import { Button } from './Button';
import { Tooltip, TooltipTrigger, TooltipContent } from './Tooltip';
import { SegmentedControl } from './SegmentedControl';
import { StatusTag } from './StatusTag';
import { useAuth } from '../context/AuthContext';
import { getSummaryByRange, OrderSummaryRow, getAllConversations } from '../services/api';

interface HeaderProps {
  className?: string;
  onMenuClick?: () => void;
}

function toLocalDateString(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function buildProductSheet(rows: OrderSummaryRow[]): object[] {
  const map = new Map<number, { Producto: string; 'Unidad de Negocio': string; Cantidad: number }>();
  for (const r of rows) {
    const qty = Number(r.quantity) || 0;
    const e = map.get(r.product_id);
    if (e) e.Cantidad += qty;
    else map.set(r.product_id, { Producto: r.product_name, 'Unidad de Negocio': r.unidad_de_negocio, Cantidad: qty });
  }
  return Array.from(map.values());
}

function buildClientSheet(rows: OrderSummaryRow[]): object[] {
  type ProductEntry = { name: string; unidad: string; qty: number };
  type GroupEntry = { clientName: string; addressName: string; address: string; products: Map<number, ProductEntry> };
  const map = new Map<string, GroupEntry>();

  for (const r of rows) {
    const clientKey = r.customer_rut ?? r.customer_name ?? 'Sin cliente';
    const address = [r.delivery_address, r.delivery_comuna].filter(Boolean).join(', ') || 'Sin dirección';
    const addressName = r.delivery_address_name || '';
    const key = `${clientKey}||${address}||${addressName}`;
    const clientName = r.customer_name ?? 'Sin cliente';

    if (!map.has(key)) map.set(key, { clientName, addressName, address, products: new Map() });
    const g = map.get(key)!;
    const p = g.products.get(r.product_id);
    if (p) p.qty += r.quantity;
    else g.products.set(r.product_id, { name: r.product_name, unidad: r.unidad_de_negocio, qty: r.quantity });
  }

  const result: object[] = [];
  Array.from(map.values()).forEach(g => {
    Array.from(g.products.values()).forEach(p => {
      result.push({ Cliente: g.clientName, 'Nombre de dirección': g.addressName, 'Dirección': g.address, Producto: p.name, 'Unidad de Negocio': p.unidad, Cantidad: p.qty });
    });
  });
  return result;
}

function buildDetailSheet(rows: OrderSummaryRow[]): object[] {
  return rows.map(r => ({
    'ID Pedido': r.order_id,
    Fecha: r.order_date,
    Hora: r.order_time,
    Cliente: r.customer_name ?? 'Sin cliente',
    RUT: r.customer_rut ?? '',
    Producto: r.product_name,
    'Unidad de Negocio': r.unidad_de_negocio,
    Cantidad: Number(r.quantity) || 0,
    'Precio unitario': r.unit_price,
  }));
}

function downloadXlsx(summary: object[], detail: object[], filename: string) {
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(summary), 'Resumen');
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(detail), 'Detalle');
  XLSX.writeFile(wb, filename);
}

const ExportPopover: React.FC<{ onClose: () => void; getToken: () => Promise<string | null> }> = ({ onClose, getToken }) => {
  const today = toLocalDateString(new Date());
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(today);
  const [mode, setMode] = useState<'product' | 'client'>('product');
  const [includeListo, setIncludeListo] = useState(true);
  const [includePendiente, setIncludePendiente] = useState(true);
  const [loading, setLoading] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('keydown', handleKey);
    document.addEventListener('mousedown', handleClick);
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.removeEventListener('mousedown', handleClick);
    };
  }, [onClose]);

  const handleDownload = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setLoading(true);
    try {
      const token = await getToken();
      const statuses: string[] = [];
      if (includeListo) statuses.push('listo');
      if (includePendiente) statuses.push('pendiente');
      const rows = await getSummaryByRange(dateFrom, dateTo, token, statuses);
      const summary = mode === 'product' ? buildProductSheet(rows) : buildClientSheet(rows);
      const detail = buildDetailSheet(rows);
      const label = mode === 'product' ? 'por-producto' : 'por-cliente';
      const fileSuffix = dateFrom === dateTo ? dateFrom : `${dateFrom}_${dateTo}`;
      downloadXlsx(summary, detail, `pedidos-${fileSuffix}-${label}.xlsx`);
      onClose();
    } catch (err) {
      console.error('[Export] error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, scale: 0.95, y: -6 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95, y: -6 }}
      transition={{ duration: 0.15 }}
      onMouseDown={e => e.stopPropagation()}
      className="absolute right-0 top-full z-[9999] w-72 bg-background border border-border rounded-lg shadow-lg p-3 flex flex-col gap-3"
    >
      {/* Date range */}
      <div className="flex gap-2">
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <label className="font-sans text-xs font-medium text-text-subtle">Desde</label>
          <input
            type="date"
            lang="es-CL"
            value={dateFrom}
            max={today}
            onChange={e => {
              setDateFrom(e.target.value);
              if (e.target.value > dateTo) setDateTo(e.target.value);
            }}
            className="font-sans text-xs text-foreground bg-muted border border-border rounded-md px-1.5 py-1.5 outline-none focus:ring-1 focus:ring-ring w-full min-w-0"
          />
        </div>
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <label className="font-sans text-xs font-medium text-text-subtle">Hasta</label>
          <input
            type="date"
            lang="es-CL"
            value={dateTo}
            min={dateFrom}
            max={today}
            onChange={e => setDateTo(e.target.value)}
            className="font-sans text-xs text-foreground bg-muted border border-border rounded-md px-1.5 py-1.5 outline-none focus:ring-1 focus:ring-ring w-full min-w-0"
          />
        </div>
      </div>

      {/* Status filter */}
      <div className="flex flex-col gap-1">
        <label className="font-sans text-xs font-medium text-text-subtle">Estado</label>
        <div className="flex flex-col gap-1">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={includeListo}
              onChange={e => setIncludeListo(e.target.checked)}
              className="accent-primary"
            />
            <StatusTag status="facturaEnviada" />
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={includePendiente}
              onChange={e => setIncludePendiente(e.target.checked)}
              className="accent-primary"
            />
            <StatusTag status="pendiente" />
          </label>
        </div>
      </div>

      {/* Segmentation */}
      <div className="flex flex-col gap-1">
        <label className="font-sans text-xs font-medium text-text-subtle">Agrupar por</label>
        <SegmentedControl
          options={[
            { label: 'Por cliente', value: 'client' },
            { label: 'Por producto', value: 'product' },
          ]}
          selectedValue={mode}
          onValueChange={v => setMode(v as 'product' | 'client')}
        />
      </div>

      {/* Action */}
      <Button
        variant="primary"
        size="sm"
        fullWidth
        loading={loading}
        disabled={!dateFrom || !dateTo || loading || (!includeListo && !includePendiente)}
        onClick={handleDownload}
        leftIcon={<DownloadSimple size={14} weight="bold" />}
      >
        Descargar Excel
      </Button>
    </motion.div>
  );
};

export const Header: React.FC<HeaderProps> = ({ className = '', onMenuClick }) => {
  const navigate = useNavigate();
  const { getAccessToken } = useAuth();
  const [exportOpen, setExportOpen] = useState(false);
  const [totalUnread, setTotalUnread] = useState(0);

  const handleLogoClick = () => navigate('/');
  const handleChatsClick = () => navigate('/chats');
  const closeExport = useCallback(() => setExportOpen(false), []);

  useEffect(() => {
    let cancelled = false;
    const fetchUnread = async () => {
      try {
        const token = await getAccessToken();
        const conversations = await getAllConversations(token);
        if (!cancelled) {
          const count = conversations.reduce((sum, c) => sum + (c.unreadCount || 0), 0);
          setTotalUnread(count);
        }
      } catch { /* non-critical */ }
    };
    fetchUnread();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={`bg-muted border-b border-border w-full ${className}`} data-name="header">
      <div className="flex items-center justify-between px-3 py-2 w-full max-w-[1280px] mx-auto" style={{ padding: '8px 12px' }}>
        <div
          className="flex items-center justify-center shrink-0 cursor-pointer"
          onClick={handleLogoClick}
          data-name="logo"
        >
          <img src="/tresmundos-logo.svg" alt="Tres Mundos" className="h-10 w-auto" />
        </div>

        <div className="flex items-center gap-2">
          {/* Export */}
          <div className="relative self-center">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setExportOpen(o => !o)}
                  aria-label="Exportar pedidos"
                  className="hover:bg-accent/5"
                >
                  <DownloadSimple size={20} weight="bold" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Descargar resumen de órdenes</TooltipContent>
            </Tooltip>

            <AnimatePresence>
              {exportOpen && (
                <ExportPopover onClose={closeExport} getToken={getAccessToken} />
              )}
            </AnimatePresence>
          </div>

          <Tooltip>
            <TooltipTrigger asChild>
              <div className="relative">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleChatsClick}
                  className="hover:bg-accent/5"
                  aria-label="Conversaciones"
                >
                  <ChatText size={20} weight="bold" />
                </Button>
                {totalUnread > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center leading-none pointer-events-none">
                    {totalUnread > 99 ? '99+' : totalUnread}
                  </span>
                )}
              </div>
            </TooltipTrigger>
            <TooltipContent>Conversaciones</TooltipContent>
          </Tooltip>

          <Button
            variant="ghost"
            size="icon"
            onClick={onMenuClick}
            className="hover:bg-accent/5"
            aria-label="Abrir menú"
          >
            <List size={20} weight="bold" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Header;
